import { useMemo } from "react";

import ace from "brace";
import "brace/mode/json";
import "brace/theme/github";
import copy from "copy-to-clipboard";
import { JsonEditor } from "jsoneditor-react";
import { isNil, omit, orderBy } from "lodash";
import { useToasts } from "react-toast-notifications";
import { twMerge } from "tailwind-merge";

import { PrimaryButton } from "@components/v2/buttons";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { Code, Tabs } from "@mantine/core";
import { type GetSalesReportV3Augmented_me_salesTableReportV3Augmented_queryMetadata_bqLogs } from "@north-beam/nb-client-side";

export type AdminBQLog = {
  bqLog: GetSalesReportV3Augmented_me_salesTableReportV3Augmented_queryMetadata_bqLogs;
  frontendContext: {
    context?: string;
    topic: string;
  };
};

export type AdminLoggingContext = {
  bqLogs: AdminBQLog[];
};

export function NoRowSelected() {
  return <div className="m-3">No row selected</div>;
}

/**
 * Safely escape a JavaScript string value for a BigQuery string literal.
 */
function escapeForBigQueryString(value: string): string {
  return `'''${value}'''`;
}

type Structable = string | number | boolean | null | undefined;

/**
 * Converts a JS object into a BigQuery STRUCT(...) string.
 * E.g. { a: "hello", b: "bellow" } -> "STRUCT('hello' AS a, 'bellow' AS b)"
 */
export function objectToStruct(obj: Record<string, Structable>): string {
  // Collect each "key => value" pair as "'value' AS key" (or numeric/bool/null)
  const parts = Object.entries(obj).map(([key, val]) => {
    if (val === null || val === undefined) {
      // If you specifically want to handle undefined vs. null differently, adjust here
      return `NULL AS ${key}`;
    } else if (typeof val === "number" || typeof val === "bigint") {
      return `${val} AS ${key}`;
    } else if (typeof val === "boolean") {
      // BigQuery expects 'true'/'false' (lowercase) or 'TRUE'/'FALSE'
      // either works, so we convert JS boolean to a lowercase string
      return `${val} AS ${key}`;
    } else {
      // Default to string
      const escaped = escapeForBigQueryString(val);
      return `${escaped} AS ${key}`;
    }
  });

  return `STRUCT(${parts.join(", ")})`;
}

/**
 * Format a value to be used in a BigQuery query string.
 *
 * @param {unknown} i The value to format. If it is:
 * - an object (but not null), it is converted to a BigQuery STRUCT string with `objectToStruct`
 * - a string, it is escaped with `escapeForBigQueryString` and wrapped in single quotes
 * - anything else, it is converted to a string with `String()`
 * @returns {string} The formatted value
 */
function formatParamValue(i: unknown): string {
  if (typeof i === "object" && i !== null) {
    return objectToStruct(i as any);
  } else if (typeof i === "string") {
    return escapeForBigQueryString(i);
  } else {
    return `${i}`;
  }
}

/**
 * Replace @param placeholders in a BigQuery query with the corresponding values
 * from the given `params` object.
 *
 * This function handles the following types of values:
 * - strings
 * - numbers
 * - arrays
 * - objects
 * - null/undefined
 *
 * @param query the BigQuery query with placeholders
 * @param params the object with values to replace the placeholders
 * @returns the modified query with placeholders replaced
 */
function replaceParams(query: string, params: Record<string, unknown>) {
  let q = query;

  orderBy(Object.keys(params), (i) => i.length, "desc").forEach((param) => {
    const paramValue = params[param];

    if (Array.isArray(paramValue)) {
      const arrayBox: string[] = [];

      paramValue.forEach((i) => {
        arrayBox.push(formatParamValue(i));
      });

      q = q.replaceAll(`@${param}`, `[${arrayBox.join(", ")}]`);
    } else {
      q = q.replaceAll(`@${param}`, formatParamValue(paramValue));
    }
  });
  return q;
}

export function Panel({
  row,
  className,
}: {
  row: AdminBQLog | null;
  className?: string;
}) {
  const { addToast } = useToasts();
  const parsedQuery = useMemo(() => {
    if (!row) {
      return null;
    }
    return replaceParams(row.bqLog.query, row.bqLog.queryParameters);
  }, [row]);

  return (
    <Tabs
      defaultValue="parsed-query"
      className={twMerge("ml-2 border w-[38vw] rounded-md", className)}
    >
      <Tabs.List>
        <Tabs.Tab className="text-xs" value="parsed-query">
          Parsed Query (params added)
        </Tabs.Tab>
        <Tabs.Tab className="text-xs" value="metadata">
          BQMetadata
        </Tabs.Tab>
        <Tabs.Tab className="text-xs" value="frontend">
          Frontend Context
        </Tabs.Tab>
        <Tabs.Tab className="text-xs" value="raw-query">
          Raw Query
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel
        value="parsed-query"
        className="overflow-auto relative"
        style={{
          height: "calc(75vh - 5rem)",
        }}
      >
        {isNil(row) ? (
          <NoRowSelected />
        ) : (
          <div className="bg-[#F1F3F5]">
            <PrimaryButton
              className="m-5"
              onClick={() => {
                addToast("COPIED TO CLIPBOARD", {
                  appearance: "success",
                });

                copy(parsedQuery ?? "");
              }}
            >
              COPY PARSED QUERY
            </PrimaryButton>
            <Code block>{parsedQuery}</Code>
          </div>
        )}
      </Tabs.Panel>
      <Tabs.Panel value="metadata">
        {isNil(row) ? (
          <NoRowSelected />
        ) : (
          <Editor
            row={omit(row.bqLog, "query")}
            key={`${row.bqLog.executionTimestamp}-${row.bqLog.queryId}-${row.bqLog.jobId}-metadata`}
          />
        )}
      </Tabs.Panel>
      <Tabs.Panel value="frontend">
        {isNil(row) ? (
          <NoRowSelected />
        ) : (
          <Editor
            row={row.frontendContext}
            key={`${row.bqLog.executionTimestamp}-${row.bqLog.queryId}-${row.bqLog.jobId}-frontend`}
          />
        )}
      </Tabs.Panel>
      <Tabs.Panel
        value="raw-query"
        className="overflow-auto relative"
        style={{
          height: "calc(75vh - 5rem)",
        }}
      >
        {isNil(row) ? (
          <NoRowSelected />
        ) : (
          <div className="bg-[#F1F3F5]">
            <PrimaryButton
              className="m-5"
              onClick={() => {
                addToast("COPIED TO CLIPBOARD", {
                  appearance: "success",
                });

                copy(row.bqLog.query ?? "");
              }}
            >
              COPY RAW QUERY
            </PrimaryButton>
            <Code block>{row.bqLog.query}</Code>
          </div>
        )}
      </Tabs.Panel>
    </Tabs>
  );
}

function Editor({ row }: { row: any }) {
  return (
    <JsonEditor
      value={row}
      mode="code"
      ace={ace}
      htmlElementProps={{ style: { height: 600 } }}
    />
  );
}
